import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { collection, query, orderBy, limit, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../lib/firebase.js';
import { useAuth } from '../../lib/auth.js';
import { categoryLabel } from '../../lib/format.js';

type Status =
  | 'uploaded'
  | 'extracting'
  | 'classifying'
  | 'embedding'
  | 'ready'
  | 'triage'
  | 'error';

type DocRow = {
  id: string;
  originalName?: string;
  category?: string;
  status?: Status;
  errorReason?: string;
  embeddingPending?: boolean;
  uploadedAt?: { seconds: number };
};

type ReprocessResult = { matched: number; refired: number; errors: number };

const STAGE_ORDER: Status[] = [
  'uploaded',
  'extracting',
  'classifying',
  'embedding',
  'ready',
  'triage',
  'error',
];

const STAGE_LABEL: Record<Status, string> = {
  uploaded: 'Queued',
  extracting: 'Extracting text',
  classifying: 'Classifying',
  embedding: 'Embedding',
  ready: 'Ready',
  triage: 'Needs review',
  error: 'Errored',
};

const STAGE_COLOR: Record<Status, string> = {
  uploaded: '#a8a29e',
  extracting: '#0ea5e9',
  classifying: '#f59e0b',
  embedding: '#8b5cf6',
  ready: '#059669',
  triage: '#f97316',
  error: '#9C4A3A',
};

const PROBLEM_STAGES: Status[] = [
  'error',
  'uploaded',
  'extracting',
  'classifying',
  'embedding',
];

function safeCategoryLabel(key?: string): string {
  if (!key) return '—';
  try {
    return categoryLabel(key);
  } catch {
    return key
      .split('_')
      .map((p) => (p ? p.charAt(0).toUpperCase() + p.slice(1) : p))
      .join(' ');
  }
}

function truncate(s: string, n: number): string {
  return s.length > n ? `${s.slice(0, n)}…` : s;
}

export function ProcessingMonitor() {
  const { role } = useAuth();
  const [docs, setDocs] = useState<DocRow[]>([]);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [openStages, setOpenStages] = useState<Partial<Record<Status, boolean>>>({
    error: true,
  });

  useEffect(() => {
    if (role !== 'admin') return;
    const q = query(
      collection(db, 'documents'),
      orderBy('uploadedAt', 'desc'),
      limit(1000),
    );
    const unsub = onSnapshot(q, (snap) => {
      const rows: DocRow[] = snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<DocRow, 'id'>) }));
      setDocs(rows);
    });
    return () => unsub();
  }, [role]);

  const counts = useMemo(() => {
    const c: Record<Status, number> = {
      uploaded: 0,
      extracting: 0,
      classifying: 0,
      embedding: 0,
      ready: 0,
      triage: 0,
      error: 0,
    };
    for (const d of docs) {
      if (d.status && d.status in c) c[d.status] += 1;
    }
    return c;
  }, [docs]);

  const byStatus = useMemo(() => {
    const m: Partial<Record<Status, DocRow[]>> = {};
    for (const d of docs) {
      if (!d.status) continue;
      (m[d.status] ??= []).push(d);
    }
    return m;
  }, [docs]);

  if (role !== 'admin') {
    return (
      <div className="mx-auto w-full max-w-5xl space-y-6 px-6 py-6">
        <div className="rounded-2xl border border-stone-200 bg-white p-5 shadow-sm">
          <p className="text-sm text-stone-600">Admins only.</p>
        </div>
      </div>
    );
  }

  const total = docs.length;

  async function refire(statuses: Status[]) {
    setRunning(true);
    setResult(null);
    try {
      const call = httpsCallable<{ statuses: Status[] }, ReprocessResult>(
        functions,
        'reprocessDocuments',
      );
      const res = await call({ statuses });
      const data = res.data;
      setResult(`Matched ${data.matched}, re-fired ${data.refired}, errors ${data.errors}.`);
    } catch (e) {
      setResult(`Failed: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setRunning(false);
    }
  }

  function toggle(stage: Status) {
    setOpenStages((prev) => ({ ...prev, [stage]: !prev[stage] }));
  }

  return (
    <div className="mx-auto w-full max-w-6xl space-y-6 px-6 py-6">
      <div className="space-y-1">
        <Link to="/documents" className="text-sm text-stone-500 hover:text-stone-700">
          ← Documents
        </Link>
        <h1 className="text-xl font-semibold text-stone-900">Processing monitor</h1>
        <p className="text-sm text-stone-500">
          Live status of every document moving through extract → classify → embed.
        </p>
      </div>

      <div className="flex flex-wrap gap-3">
        {STAGE_ORDER.map((stage) => {
          const count = counts[stage];
          const alwaysShow = stage === 'ready' || stage === 'error';
          if (count === 0 && !alwaysShow) return null;
          return (
            <div
              key={stage}
              className="rounded-2xl border border-stone-200 bg-white p-5 shadow-sm"
            >
              <div className="flex items-center gap-2">
                <span
                  className="inline-block h-2.5 w-2.5 rounded-full"
                  style={{ backgroundColor: STAGE_COLOR[stage] }}
                />
                <span className="text-[10px] uppercase tracking-wider text-stone-400">
                  {STAGE_LABEL[stage]}
                </span>
              </div>
              <div className="mt-2 text-2xl font-semibold text-stone-900">{count}</div>
            </div>
          );
        })}
      </div>

      <div className="space-y-2">
        <div className="flex h-3 w-full overflow-hidden rounded-full bg-stone-100">
          {total > 0 &&
            STAGE_ORDER.map((stage) => {
              const count = counts[stage];
              if (count === 0) return null;
              return (
                <div
                  key={stage}
                  title={`${STAGE_LABEL[stage]}: ${count}`}
                  style={{
                    backgroundColor: STAGE_COLOR[stage],
                    width: `${(count / total) * 100}%`,
                  }}
                />
              );
            })}
        </div>
        <p className="text-xs text-stone-500">
          {counts.ready}/{total} ready
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-stone-200 bg-white p-5 shadow-sm">
        <button
          type="button"
          disabled={running}
          onClick={() => refire(['error', 'uploaded'])}
          className="rounded bg-[color:var(--sfp-primary)] px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50"
        >
          Re-fire stuck &amp; errored
        </button>
        <button
          type="button"
          disabled={running}
          onClick={() => refire(['error'])}
          className="rounded border border-stone-300 px-3 py-1.5 text-sm font-medium text-stone-700 disabled:opacity-50"
        >
          Re-fire errored only
        </button>
        {result && <span className="text-sm text-stone-600">{result}</span>}
      </div>

      <div className="space-y-4">
        {PROBLEM_STAGES.map((stage) => {
          const rows = byStatus[stage] ?? [];
          if (rows.length === 0) return null;
          const open = openStages[stage] ?? false;
          return (
            <div
              key={stage}
              className="rounded-2xl border border-stone-200 bg-white p-5 shadow-sm"
            >
              <button
                type="button"
                onClick={() => toggle(stage)}
                className="flex w-full items-center gap-2 text-left"
              >
                <span
                  className="inline-block h-2.5 w-2.5 rounded-full"
                  style={{ backgroundColor: STAGE_COLOR[stage] }}
                />
                <span className="text-[10px] uppercase tracking-wider text-stone-400">
                  {STAGE_LABEL[stage]}
                </span>
                <span className="text-sm font-medium text-stone-700">{rows.length}</span>
                <span className="ml-auto text-stone-400">{open ? '▾' : '▸'}</span>
              </button>
              {open && (
                <ul className="mt-3 divide-y divide-stone-100">
                  {rows.slice(0, 25).map((d) => (
                    <li key={d.id} className="py-2">
                      <Link to={`/documents/${d.id}`} className="block hover:bg-stone-50">
                        <div className="flex items-center justify-between gap-3">
                          <span className="truncate text-sm font-medium text-stone-800">
                            {d.originalName ?? d.id}
                          </span>
                          <span className="shrink-0 text-xs text-stone-400">
                            {safeCategoryLabel(d.category)}
                          </span>
                        </div>
                        {stage === 'error' && d.errorReason && (
                          <p className="mt-1 text-xs" style={{ color: '#9C4A3A' }}>
                            {truncate(d.errorReason, 160)}
                          </p>
                        )}
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}
      </div>

      <p className="text-xs text-stone-400">
        embeddingPending docs are fully usable; they just await semantic-search embedding.
      </p>
    </div>
  );
}
